import { SectionContainer } from '@/modules/pmt/components/SectionContainer';
import type { IShipmentData, IShipmentLineItem } from '@/modules/pmt/interfaces';
import { type FC, useEffect, useState } from 'react';
import { TRACKING_DETAILS_SECTIONS } from '../../constants';
import { OrderAmountContainer, OrderLineItemsContainer, OrderSectionContainer } from './OrderSection.styles';
import { OrderAmount } from './components/OrderAmount';
import { OrderLineItem } from './components/OrderLineItem';

interface OrderSectionProps {
  shipmentData: IShipmentData;
  isOwnShipment: boolean;
}

export const OrderSection: FC<OrderSectionProps> = ({ shipmentData, isOwnShipment }) => {
  const { orderTracking, lineItems, shipmentValue, additionalInfo, totalShippingCost, firestoreDocId } = shipmentData;

  const [isOpen, setIsOpen] = useState(true);
  // Reset isOpen when firestoreDocId changes, because this comp is never unmounted on desktop
  // Cannot use parcelId because it is not unique
  // biome-ignore lint/correctness/useExhaustiveDependencies: setIsOpen is stable
  useEffect(() => {
    setIsOpen(true);
  }, [firestoreDocId]);

  const isShowLineItemsSection = !!lineItems?.length && lineItems.some((item: IShipmentLineItem) => !!item.productName);

  const isShowAmountSection = !!(shipmentValue || totalShippingCost || additionalInfo?.orderTotalPrice);

  //  Order Section is shown if any of the following is shown:
  //  1. Order ID
  //  2. Line items
  //  3. Amount section
  const isShowOrderSection = !!orderTracking?.orderId || isShowLineItemsSection || isShowAmountSection;

  if (!isShowOrderSection) return null;

  const renderOrderLineItemsSection = () => {
    if (!isShowLineItemsSection) return null;
    const hasAtLeastOneLineItemImage = lineItems.some((item) => !!item.productImageUrl);

    return (
      <OrderLineItemsContainer>
        {lineItems.map(
          (
            { productName, quantity, productImageUrl, productUrl, totalPrice, currencyCode }: IShipmentLineItem,
            index,
          ) => {
            const productCost =
              typeof totalPrice === 'number' || typeof totalPrice === 'string'
                ? Number.parseFloat(totalPrice).toFixed(2)
                : totalPrice;

            return (
              <OrderLineItem
                key={`${index}-${quantity}`}
                title={productName}
                quantity={quantity}
                imageUrl={
                  hasAtLeastOneLineItemImage ? productImageUrl || '/images/icons/product-placeholder.svg' : undefined
                }
                productUrl={productUrl}
                cost={productCost && currencyCode ? `${productCost} ${currencyCode}` : productCost}
              />
            );
          },
        )}
      </OrderLineItemsContainer>
    );
  };

  const renderAmountSection = () => {
    if (!isShowAmountSection) return null;
    return (
      <OrderAmountContainer>
        <OrderAmount
          subtotal={shipmentValue}
          tax={additionalInfo?.orderTotalTax}
          shippingFee={totalShippingCost}
          discounts={additionalInfo?.discounts}
          total={additionalInfo?.orderTotalPrice}
        />
      </OrderAmountContainer>
    );
  };

  const showContent = (isShowLineItemsSection || isShowAmountSection) && (shipmentData.isSMEShipment || isOwnShipment);

  return (
    <SectionContainer>
      <SectionContainer.Header
        sectionId={orderTracking?.orderId}
        sectionIcon={TRACKING_DETAILS_SECTIONS.Order.icon}
        sectionTitle={TRACKING_DETAILS_SECTIONS.Order.titleKey}
        gutterBottom={showContent}
      />
      {showContent && (
        <SectionContainer.CollapsibleContent isOpen={isOpen} handleToggle={() => setIsOpen((prev) => !prev)}>
          <OrderSectionContainer>
            {/* Line Items Section */}
            {renderOrderLineItemsSection()}
            {/* Amount Section */}
            {renderAmountSection()}
          </OrderSectionContainer>
        </SectionContainer.CollapsibleContent>
      )}
    </SectionContainer>
  );
};
